import React from 'react';
import { SpellCheckMatch } from '@/services/spellCheckerService';
import { 
  getSpellingErrors, 
  getGrammarErrors, 
  getStyleErrors,
  categorizeErrors 
} from '@/utils/spellCheckerUtils';

interface SpellCheckerSidebarProps {
  spellCheckResults: SpellCheckMatch[];
  isChecking: boolean;
  onSuggestionClick: (match: SpellCheckMatch, suggestion: string) => void;
  onIgnoreError: (match: SpellCheckMatch) => void;
  onWordClick?: (match: SpellCheckMatch) => void;
  className?: string;
}

const SpellCheckerSidebar: React.FC<SpellCheckerSidebarProps> = ({
  spellCheckResults,
  isChecking,
  onSuggestionClick,
  onIgnoreError,
  onWordClick,
  className = ''
}) => {
  // Use comprehensive categorization
  const categorized = categorizeErrors(spellCheckResults);
  const { spelling, grammar, style } = categorized;

  if (isChecking) {
    return (
      <div className={`bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 w-80 ${className}`}>
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 dark:border-blue-400 mr-2"></div>
            <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">Checking spelling...</h3>
          </div>
        </div>
      </div>
    );
  }

  if (spellCheckResults.length === 0) {
    return (
      <div className={`bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 w-80 ${className}`}>
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-green-500 dark:text-green-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">No issues found</h3>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Your text looks good!</p>
        </div>
      </div>
    );
  }

  const renderErrorSection = (
    errors: SpellCheckMatch[], 
    title: string, 
    icon: React.ReactNode, 
    colorClass: string
  ) => {
    if (errors.length === 0) return null;

    return (
      <div className="border-b border-gray-200 dark:border-gray-700">
        <div className={`px-4 py-2 ${colorClass} dark:bg-opacity-20 border-b border-gray-100 dark:border-gray-700`}>
          <div className="flex items-center">
            {icon}
            <h4 className="text-xs font-medium ml-2 text-gray-900 dark:text-gray-100">{title} ({errors.length})</h4>
          </div>
        </div>
        <div className="divide-y divide-gray-100 dark:divide-gray-700">
          {errors.map((error, index) => (
            <div key={`${title.toLowerCase()}-${index}`} className="p-4">
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
                    <span>{index + 1}.</span>
                    {onWordClick ? (
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onWordClick(error);
                        }}
                        className={`underline cursor-pointer ml-1 ${
                          title === 'Spelling' ? 'text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300' :
                          title === 'Grammar' ? 'text-yellow-600 dark:text-yellow-400 hover:text-yellow-800 dark:hover:text-yellow-300' :
                          'text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300'
                        }`}
                      >
                        "{error.context.text.slice(error.context.offset, error.context.offset + error.context.length)}"
                      </button>
                    ) : (
                      <span className="ml-1"> "{error.context.text.slice(error.context.offset, error.context.offset + error.context.length)}"</span>
                    )}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">{error.message}</p>
                </div>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onIgnoreError(error);
                  }}
                  className="text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 ml-2"
                >
                  Ignore
                </button>
              </div>
              <div className="space-y-1">
                {error.replacements.slice(0, 3).map((replacement, repIndex) => (
                  <button
                    key={repIndex}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onSuggestionClick(error, replacement.value);
                    }}
                    className={`w-full text-left px-3 py-2 text-sm rounded-md transition-colors ${
                      title === 'Spelling' ? 'bg-red-50 dark:bg-red-900/30 hover:bg-red-100 dark:hover:bg-red-900/50' :
                      title === 'Grammar' ? 'bg-yellow-50 dark:bg-yellow-900/30 hover:bg-yellow-100 dark:hover:bg-yellow-900/50' :
                      'bg-purple-50 dark:bg-purple-900/30 hover:bg-purple-100 dark:hover:bg-purple-900/50'
                    }`}
                  >
                    <div className={`font-medium ${
                      title === 'Spelling' ? 'text-red-900 dark:text-red-200' :
                      title === 'Grammar' ? 'text-yellow-900 dark:text-yellow-200' :
                      'text-purple-900 dark:text-purple-200'
                    }`}>
                      {replacement.value}
                    </div>
                    {replacement.shortDescription && (
                      <div className={`text-xs ${
                        title === 'Spelling' ? 'text-red-700 dark:text-red-300' :
                        title === 'Grammar' ? 'text-yellow-700 dark:text-yellow-300' :
                        'text-purple-700 dark:text-purple-300'
                      }`}>
                        {replacement.shortDescription}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className={`bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 overflow-y-auto ${className}`}>
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-red-500 dark:text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">Writing Issues</h3>
          </div>
          <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-200 dark:bg-gray-700 px-2 py-1 rounded-full">
            {spellCheckResults.length}
          </span>
        </div>
      </div>

      {/* Spelling Errors Section */}
      {renderErrorSection(
        spelling,
        'Spelling',
        <svg className="w-4 h-4 text-red-500" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
        </svg>,
        'bg-red-50'
      )}

      {/* Grammar Errors Section (including punctuation) */}
      {renderErrorSection(
        grammar,
        'Grammar',
        <svg className="w-4 h-4 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
        </svg>,
        'bg-yellow-50'
      )}

      {/* Style Errors Section */}
      {renderErrorSection(
        style,
        'Style',
        <svg className="w-4 h-4 text-purple-500" fill="currentColor" viewBox="0 0 20 20">
          <path d="M11 3a1 1 0 10-2 0v1a1 1 0 102 0V3zM15.657 5.757a1 1 0 00-1.414-1.414l-.707.707a1 1 0 001.414 1.414l.707-.707zM18 10a1 1 0 01-1 1h-1a1 1 0 110-2h1a1 1 0 011 1zM5.05 6.464A1 1 0 106.464 5.05l-.707-.707a1 1 0 00-1.414 1.414l.707.707zM5 10a1 1 0 01-1 1H3a1 1 0 110-2h1a1 1 0 011 1zM8 16v-1h4v1a2 2 0 11-4 0zM12 14c.015-.34.208-.646.477-.859a4 4 0 10-4.954 0c.27.213.462.519.476.859h4.002z" />
        </svg>,
        'bg-purple-50'
      )}
    </div>
  );
};

export default SpellCheckerSidebar; 